import {NumberInput, Paper} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import { Info as IconInfoCircle } from "@phosphor-icons/react";
import {Fragment, useMemo, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import Label from "../../components/Label/Label";
import {clamp} from "../map/utils";
import {chartPalette} from "./chartUtils";
import {doors} from "./doorUtils";


export const items = [
    {id: 255, name: 'Alpha Plus Charge', firepower: 20, min: 15, max: 28, hits: 1},
    {id: 254, name: 'Blaststick', firepower: 15, min: 10, max: 16, hits: 1},
    {id: 207, name: 'Class-A Thermal Detonator', firepower: 24, min: 20, max: 45, hits: 1},
    {id: 253, name: 'Frame Charge', firepower: 18, min: 20, max: 30, hits: 1},
    {id: 251, name: 'PrG-7', firepower: 23, min: 40, max: 60, hits: 1},
    {id: 256, name: 'Sapper Charge', firepower: 20, min: 30, max: 40, hits: 1},
    {id: 257, name: 'Sequencer Charge', firepower: 21, min: 50, max: 70, hits: 1},
    {id: 252, name: 'Thermal Bomb', firepower: 29, min: 40, max: 80, hits: 1},
];


const skill = [0, 1, 2, 3, 4, 5];
export const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const chanceArm = (num, hw) => clamp(75 - (15 * num) + (15 * hw), 0, 100);

const chartChance = nums.map(n => {
    const row = {charges: n};
    skill.forEach(s => { row[s] = chanceArm(n, s); });
    return row;
});
const chanceSeries = skill.map((s, i) => ({name: String(s), color: chartPalette[i]}));

const getMaxRounds = (item, door, num, skill) => {
    let chance = chanceArm(num, skill);
    let Fmod = item.firepower / (item.firepower + door.armour);
    let dmg = Math.round(item.min * Fmod * num);
    return Math.ceil(door.hull / (dmg * (chance / 100)));
}


export default function DoorDemolition() {

    const [skillHW, setSkillHW] = useState(0);
    const [skillRD, setSkillRD] = useState(0);
    const [skillRepair, setSkillRepair] = useState(0);
    const [num, setNum] = useState(1);

    const skillExplosive = useMemo(() => {
        return Math.max(skillHW, Math.floor(skillRD * 0.66));
    }, [skillHW, skillRD]);

    let tta = 30 + (5 * (num - 1));
    let chanceA = chanceArm(num, skillHW);
    let chanceD = clamp(75 - (15 * num) + (10 * Math.max(skillRepair, skillHW)), 0, 100);

    return (
        <div className="w-full">
            <title>Door Demolition</title>
            <Row align="center">
                <Col><h1>Demolition</h1></Col>
                <Col grow={1}/>
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Demolitions" target="_blank" rel="noreferrer"><Icon icon={IconMap.SwcRules}/> Full
                    rules</a></Col>
            </Row>
            <Paper className="mb-3">
                <h3 className="mb-0 mt-0">Skills</h3>
                <Row align="end" mb={4}>
                    <Col>
                        <Label>Heavy Weapons</Label>
                        <NumberInput value={skillHW} onChange={setSkillHW} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>R&D Metallurgy</Label>
                        <NumberInput value={skillRD} onChange={setSkillRD} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Repair</Label>
                        <NumberInput value={skillRepair} onChange={setSkillRepair} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Explosive</Label>
                        <NumberInput value={skillExplosive} disabled min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col grow={1}/>
                    <Col>
                        <Label><label htmlFor="num">Explosive Number</label></Label>
                        <NumberInput id="num" value={num} onChange={setNum} min={1} max={10} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <b>Time to Arm</b>: {tta} minutes<br/>
                        <b>Chance of Arming</b>: {chanceA}%<br/>
                        <b>Chance of Disarming</b>: {chanceD}%<br/>
                    </Col>
                </Row>

                <table className="w-full">
                    <thead>
                    <tr>
                        <th/>
                        {doors.map(d => <th key={d.lvl} colSpan={3}>{d.name}</th>)}
                    </tr>
                    <tr>
                        <th>Item</th>
                        {doors.map(d => <Fragment key={d.lvl}>
                            <th>Damage</th>
                            <th>Rounds</th>
                            <th>AVG Time</th>
                        </Fragment>)}
                    </tr>
                    </thead>
                    <tbody>
                    {items.map(item => <ExplosiveRow
                        key={item.id}
                        item={item}
                        num={num}
                        chanceA={chanceA}
                        tta={tta}
                    />)}
                    </tbody>
                </table>

                <p className="text-blue-200"><IconInfoCircle size={16}/> <i>Rounds are not rounds of combat but how many times the action has to
                    be repeated. Each time it will take the Time to Arm + 4 hours for the explosive countdown</i></p>

            </Paper>

            <Paper className="mb-3">
                <h2 className="mt-0">Chance of arming by Heavy Weapon Skill</h2>
                <LineChart h={300} data={chartChance} series={chanceSeries} dataKey="charges" withLegend curveType="linear" xAxisLabel="Number of charges" yAxisLabel="%" />
            </Paper>

            {doors.map(door => <DoorGraph key={door.lvl} door={door}/>)}
        </div>
    )
}


const ExplosiveRow = ({item, num, chanceA, tta}) => {

    return (
        <tr>
            <th className="text-left">{item.name}</th>
            {doors.map(d => {
                let Fmod = item.firepower / (item.firepower + d.armour);
                let totDmin = Math.round(item.min * Fmod * num);
                let totDmax = Math.round(item.max * Fmod * num);
                let roundMin = Math.ceil(d.hull / (totDmax * (chanceA / 100)));
                let roundMax = Math.ceil(d.hull / (totDmin * (chanceA / 100)));

                let tpr = (4 + (tta / 60));

                let dAvg = Math.round((item.min + item.max) / 2) * Fmod * num;
                let roundAVG = Math.round(d.hull / (dAvg * (chanceA / 100)));
                let minTime = Math.round(roundMin * tpr)
                let maxTime = Math.round(roundMax * tpr)
                let time = Math.round(roundAVG * tpr);

                return (
                    <Fragment key={d.lvl}>
                        <td>{totDmin} - {totDmax}</td>
                        <td>{chanceA > 0 ? <span>{roundMin} - {roundMax}</span> : <i>Impossible</i>}</td>
                        <td>{chanceA > 0 ? <span title={`${minTime}H - ${maxTime}H`}>{time}H</span> : <i>-</i>}</td>
                    </Fragment>
                );

            })}
        </tr>
    )

}


const doorGraphSeries = items.map((item, i) => ({name: item.name, color: chartPalette[i % chartPalette.length]}));

const DoorGraph = ({door}) => {

    const [skill, setSkill] = useState(0);
    const chartDoor = useMemo(() => nums.map(n => {
        const row = {charges: n};
        items.forEach(item => { row[item.name] = getMaxRounds(item, door, n, skill); });
        return row;
    }), [skill, door]);


    return (
        <Paper className="mb-3">
            <Row align="center" mb={2}>
                <Col>
                    <h2 className="mt-0 mb-0">{door.name} Door</h2>
                    <h4>Rounds necessary based on number of charges in each round</h4>
                </Col>
                <Col grow={1} />
                <Col>
                    <label htmlFor={`skill-${door.lvl}`} className="mr-2">HW Skill</label>
                    <NumberInput id={`skill-${door.lvl}`} value={skill} onChange={setSkill} min={0} max={5} style={{width: 80}}/>
                </Col>
            </Row>
            <LineChart h={300} data={chartDoor} series={doorGraphSeries} dataKey="charges" withLegend curveType="linear" xAxisLabel="Number of charges" yAxisLabel="Rounds" />
        </Paper>
    )
}
